import apisauce from 'apisauce';
import { removeEmpty } from '../helpers/object';

const apiMonitor = response => console.log("api response", response);

const create = (baseURL = process.env.REACT_APP_API_URL) => {
  // ------
  // STEP 1
  // ------
  //
  // Create and configure an apisauce-based api object.
  //
  const api = apisauce.create({
    // base URL is read from the "constructor"
    baseURL,
    // here are some default headers
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      'Accept-Token-Algorithm': 'RS256',
      //'Authorization': 'BeareFLdc0w',
    },
    // 300 second timeout...
    timeout: 300000
  });

  if (process.env.NODE_ENV !== 'production') {
    api.addMonitor(apiMonitor);
  }

  // ------
  // STEP 2
  // ------
  //
  // Define some functions that call the api.  The goal is to provide
  // a thin wrapper of the api layer providing nicer feeling functions
  // rather than "get", "post" and friends.
  //
  // I generally don't like wrapping the output at this level because
  // sometimes specific actions need to be take on `403` or `401`, etc.
  //
  // Since we can't hide from that, we embrace it by getting out of the
  // way at this level.
  //

  // == AUTH ==
  const setAuthToken = access_token =>
    api.setHeaders({ 'Authorization': `Bearer ${access_token}` });

  const removeAuthToken = () => api.setHeader('Authorization', null);

  const setLanguage = id =>
    api.setHeaders({ 'Content-Language': id });

  // == PROFILE ==
  const getUserProfile = () => api.get('me');

  // == DICTIONARIES ==
  const getDictionaries = (name, query) => api.get(`dic/${name}`, query && { query });

  // == GLOBAL SEARCH ==
  const getSearch = params => api.get('search', removeEmpty(params));

  // == ADMINS ==
  // == ADMINS / DIAGNOSTIC ==
  const getDiagnostic = () => api.get(`admin/diagnostic`);

  // == ADMINS / DEALERS ==
  const getDealers = ({ page, query, filter, order }) =>
    api.get('admin/dealers', removeEmpty({ page, query, filter, order }));

  const getDealer = id => api.get(`admin/dealers/${id}`);

  const addDealer = dealer => api.post('admin/dealers', dealer);

  const editDealer = dealer => api.put(`admin/dealers/${dealer.id}`, dealer);

  const deleteDealer = id => api.delete(`admin/dealers/${id}`);

  // == ADMINS / USERS ==
  const getUsers = ({ page, query, filter, order }) =>
    api.get('admin/users', removeEmpty({ page, query, filter, order }));

  const getUser = id => api.get(`admin/users/${id}`);

  const addUser = user => api.post('admin/users', user);

  const editUser = user => api.put(`admin/users/${user.id}`, user);

  const deleteUser = id => api.delete(`admin/users/${id}`);

  // == REPORTS ==
  const getReports = ({ page, query, filter, order }) =>
    api.get('reports', removeEmpty({ page, query, filter, order }));

  const getReport = id => api.get(`reports/${id}`);

  const addReport = report => api.post('reports', report);

  const editReport = report => api.put(`reports/${report.id}`, report);

  const deleteReport = id => api.delete(`reports/${id}`);

  // == MAILING ==
  const getNotifications = ({ page, query, filter, order }) =>
    api.get('mailing', removeEmpty({ page, query, filter, order }));

  const getNotification = id => api.get(`mailing/${id}`);

  const addNotification = notification => api.post('mailing', notification);

  const editNotification = notification => api.put(`mailing/${notification.id}`, notification);

  const deleteNotification = id => api.delete(`mailing/${id}`);

  // == CLIENTS ==
  const getClients = ({ page, query, filter, order }) =>
    api.get('clients', removeEmpty({ page, query, filter, order }));

  const getClient = id => api.get(`clients/${id}`);

  const addClient = user => api.post('clients', user);

  const editClient = user => api.put(`clients/${user.id}`, user);

  const deleteClient = id => api.delete(`clients/${id}`);

  // == CARS ==
  const checkCar = (vin, epts) => api.get(`cars/search/${vin}/${epts}`);

  const addCar = (id, car) => api.post(`clients/${id}/cars/${car.id}`, car);

  const editCar = car => api.put(`cars/${car.id}`, car);

  const deleteCar = id => api.delete(`cars/${id}`);

  // == CAR'S DTC ERRORS == //
  const getCarErrors = carId => api.get(`cars/${carId}/dtcerrors`);

  const deleteCarErrors = carId => api.delete(`cars/${carId}/dtcerrors`);

  const getCarErrorsArchive = ({ id, page, query, filter, order }) =>
    api.get(`cars/${id}/dtcerrors/history`, removeEmpty({ page, query, filter, order }));

  // == FOTA == //
  const getFirmwareCars = ({ page, query, filter, order }) =>
    api.get('fota/cars', removeEmpty({ page, query, filter, order }));

  const getDownloadUrlXlsCars = ({ page, query, filter, order }) =>
    api.get('fota/campaign/cars/report', removeEmpty({ page, query, filter, order }), {responseType: 'blob'});

  const getFotaCar = id => api.get(`cars/fota/${id}`);
  const editFotaCar = fotaCar => api.put(`cars/fota/${fotaCar.id}`, fotaCar);
  const addFotaCar = fotaCar => api.post(`cars/fota`, fotaCar);
  const deleteFotaCar = id => api.delete(`cars/fota/${id}`);

  const getFirmwares = ({ page, query, filter, order }) =>
    api.get('fota/firmware', removeEmpty({ page, query, filter, order }));
  const getFirmware = id => api.get(`fota/firmware/${id}`);
  const editFirmware = firmware => api.put(`fota/firmware`, firmware);
  const addFirmwareFile = (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`fota/firmware/file/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  };
  const getFirmwareFile = id => api.get(`fota/firmware/file/${id}`, {}, { responseType: 'blob' });
  const addFirmware = firmware => api.post(`fota/firmware`, firmware);
  const deleteFirmware = id => api.delete(`fota/firmware/${id}`);
  const addFirmwareComments = (firmwareId, data) => api.post(`fota/firmware/comments/${firmwareId}`, data);
  const getFirmwareComments = (firmwareId) => api.get(`fota/firmware/comments/${firmwareId}`);

  const getCampaigns = ({ page, query, filter, order }) =>
    api.get('fota/campaign', removeEmpty({ page, query, filter, order }));

  const getCampaignDetail = campaignId => api.get(`fota/campaign/${campaignId}`);
  const addCampaign = data => api.post(`fota/campaign`, data);
  const editCampaign = campaign => api.put('fota/campaign', campaign);
  const deleteCampaign = id => api.delete(`fota/campaign/${id}`);

  const uploadVinList = (firmwareId, vins) => {
    if (firmwareId) {
      return api.post(`fota/campaign/vins/${firmwareId}`, vins)
    }

    return api.post(`fota/campaign/vins`, vins)
  }
  const uploadCarsFilter = (firmwareId, filter) => {
    if (firmwareId) {
      api.post(`fota/campaign/filter/${firmwareId}`, filter)
    }

    return api.post(`fota/campaign/filter`, filter);
  }
  const uploadVinListFile = (firmwareId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    if (firmwareId) {
      return api.post(`fota/campaign/upload-vins`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }

    return api.post(`fota/campaign/upload-vins`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // == BILLING ==
  const getBilling = ({ page, query, filter, order }) =>
    api.get('billing', removeEmpty({ page, query, filter, order }));
  
  const getDownloadUrlXlsBilling = ({ page, query, filter, order }) =>
    api.get('reports/billing', removeEmpty({ page, query, filter, order }), {responseType: 'blob'});

  // ------
  // STEP 3
  // ------
  //
  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  // methods in step 2.
  //
  // Notice we're not returning back the `api` created in step 1?  That's
  // because it is scoped privately.  This is one way to create truly
  // private scoped goodies in JavaScript.
  //
  return {
    // a list of the API functions from step 2
    setAuthToken,
    removeAuthToken,
    setLanguage,
    getDiagnostic,
    getUserProfile,
    getDictionaries,
    getSearch,
    getDealers,
    getDealer,
    addDealer,
    editDealer,
    deleteDealer,
    getUsers,
    getUser,
    addUser,
    editUser,
    deleteUser,
    getReports,
    getReport,
    addReport,
    editReport,
    deleteReport,
    getNotifications,
    getNotification,
    addNotification,
    editNotification,
    deleteNotification,
    getClients,
    getClient,
    addClient,
    editClient,
    deleteClient,
    getCarErrors,
    checkCar,
    addCar,
    editCar,
    deleteCar,
    deleteCarErrors,
    getCarErrorsArchive,
    getFirmwareCars,
    getDownloadUrlXlsCars,
    getFotaCar,
    editFotaCar,
    addFotaCar,
    deleteFotaCar,
    getFirmwares,
    getFirmware,
    editFirmware,
    addFirmware,
    addFirmwareFile,
    getFirmwareFile,
    deleteFirmware,
    getCampaigns,
    getCampaignDetail,
    editCampaign,
    deleteCampaign,
    addCampaign,
    addFirmwareComments,
    getFirmwareComments,
    uploadVinListFile,
    uploadCarsFilter,
    uploadVinList,
    getBilling,
    getDownloadUrlXlsBilling
  };
};

// let's return back our create method as the default.
export default {
  create
};
